/**
 * Search Widget Defaults Composable
 *
 * Resolves the initial search state for the standalone search widget variant.
 *
 * Unlike `useFilterInitializer`, this hook ignores URL, sessionStorage and
 * localStorage — the widget is "fresh" on every page load. The only inputs
 * are `ibeSearchDefaults` (per-page backend defaults, including forced
 * fields) and a hardcoded fallback (today + 7 days, two adults).
 *
 * Forced fields (`override-user: true`) lock their value and surface as
 * disabled buttons in the SearchBar via `disabledFields`.
 */

import { isFieldForced } from '@/stores/filterConfiguration';
import { loadIbeSearchDefaults } from '@/composables/filters/useSearchDefaults';
import {
  parseDatesFromConfig,
  parsePaxConfigString,
  getHardcodedDefaults,
} from '@/utils/searchDefaults/searchDefaultsResolver';
import { FilterValidation } from '@/utils/validation/filterValidation';
import { nightsInRange } from '@/utils/filters/durationMode';
import type { DateRange, RoomOccupancy } from '@/types/SearchBar';
import type { DisabledParameterField, IbeSearchDefaults } from '@/types/SearchDefaults';

export interface SearchWidgetInitialState {
  selectedCountries: string[];
  dateRange: DateRange;
  duration: number;
  roomOccupancy: RoomOccupancy;
}

export interface SearchWidgetDefaults {
  initialState: SearchWidgetInitialState;
  disabledFields: DisabledParameterField[];
  /** Raw `ibeSearchDefaults` so callers can hydrate other stores without re-reading the global. */
  defaults: IbeSearchDefaults | null;
}

export function loadSearchWidgetDefaults(): SearchWidgetDefaults {
  const defaults = loadIbeSearchDefaults();
  const fallback = getHardcodedDefaults();

  // Destinations
  const destValue = defaults?.dest?.value;
  const selectedCountries = Array.isArray(destValue)
    ? destValue.map((c) => (typeof c === 'string' ? c.toUpperCase() : c))
    : [];

  // Date range
  const configDateRange = parseDatesFromConfig(defaults);
  const dateRange: DateRange = configDateRange ?? fallback.dateRange;

  // Duration
  const daysStr = defaults?.days?.value;
  const validatedDuration =
    daysStr !== undefined ? FilterValidation.validateDuration(daysStr) : null;
  const windowNights = nightsInRange(dateRange);
  const duration =
    validatedDuration ??
    (windowNights > 0 ? Math.min(fallback.duration, windowNights) : fallback.duration);

  // Pax
  const paxStr = defaults?.pax?.value;
  const configRoomOccupancy = paxStr ? parsePaxConfigString(paxStr) : undefined;
  const roomOccupancy = configRoomOccupancy ?? fallback.roomOccupancy;

  const disabledFields: DisabledParameterField[] = [];
  if (isFieldForced(defaults?.dest)) disabledFields.push('dest');
  if (
    isFieldForced(defaults?.date) ||
    isFieldForced(defaults?.todate) ||
    isFieldForced(defaults?.days)
  ) {
    disabledFields.push('date');
  }
  if (isFieldForced(defaults?.pax)) disabledFields.push('pax');

  return {
    initialState: {
      selectedCountries,
      dateRange,
      duration,
      roomOccupancy,
    },
    disabledFields,
    defaults,
  };
}
